import { createTheme, type ThemeOptions } from '@mui/material/styles';

// MD3 Surface container elevation system (dark theme as default)
// Values approximate MD3 tonal palette tokens

const commonTheme: ThemeOptions = {
  typography: {
    fontFamily: '"Noto Sans SC", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    h1: { fontWeight: 700, letterSpacing: '-0.5px' },
    h2: { fontWeight: 700, letterSpacing: '-0.5px' },
    h3: { fontWeight: 700 },
    h4: { fontWeight: 600 },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
    subtitle1: { fontWeight: 500 },
    button: { textTransform: 'none', fontWeight: 600 },
    caption: { fontSize: '0.75rem' },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 12, textTransform: 'none', fontWeight: 600, padding: '10px 20px', minHeight: 44 },
        sizeSmall: { padding: '6px 14px', minHeight: 36, fontSize: '0.8125rem' },
        sizeLarge: { padding: '14px 28px', minHeight: 52, fontSize: '1rem' },
      },
    },
    MuiCard: {
      styleOverrides: { root: { borderRadius: 16 } },
    },
    MuiPaper: {
      styleOverrides: {
        root: { borderRadius: 16 },
        outlined: { borderRadius: 16 },
      },
    },
    MuiChip: {
      styleOverrides: { root: { borderRadius: 8 }, sizeSmall: { height: 28, fontSize: '0.75rem' } },
    },
    MuiBottomNavigation: {
      styleOverrides: {
        root: { borderTop: 'none', height: 64 },
      },
    },
    MuiBottomNavigationAction: {
      styleOverrides: {
        root: { minWidth: 48, padding: '6px 0 10px', '&.Mui-selected': { fontSize: '0.75rem' } },
      },
    },
    MuiLinearProgress: {
      styleOverrides: { root: { borderRadius: 4, height: 4 } },
    },
    MuiTextField: {
      styleOverrides: { root: { borderRadius: 12, '& .MuiOutlinedInput-root': { borderRadius: 12 } } },
    },
    MuiCheckbox: {
      styleOverrides: { root: { borderRadius: 4 } },
    },
    MuiAlert: {
      styleOverrides: { standard: { borderRadius: 12 } },
    },
    MuiTooltip: {
      styleOverrides: { tooltip: { borderRadius: 8, fontSize: '0.75rem' } },
    },
  },
};

// Dark theme — MD3 dark scheme with purple primary
export const darkTheme = createTheme({
  ...commonTheme,
  palette: {
    mode: 'dark',
    primary: { main: '#D0BCFF', light: '#EADDFF', dark: '#9A82DB' },
    secondary: { main: '#CCC2DC', light: '#E8DEF8', dark: '#958DA5' },
    error: { main: '#F2B8B5' },
    background: {
      default: '#141218',
      paper: '#211F26',
    },
    text: { primary: '#E6E1E5', secondary: '#CAC4D0', disabled: '#938F99' },
    divider: '#49454F',
    action: { hover: 'rgba(208, 188, 255, 0.08)', selected: 'rgba(208, 188, 255, 0.16)' },
  },
  shadows: [
    'none', '0px 1px 2px rgba(0,0,0,0.3)', '0px 2px 6px rgba(0,0,0,0.32)',
    '0px 1px 2px rgba(0,0,0,0.3), 0px 2px 6px rgba(0,0,0,0.24)',
    '0px 1px 3px rgba(0,0,0,0.3), 0px 4px 8px rgba(0,0,0,0.24)',
    '0px 1px 4px rgba(0,0,0,0.3), 0px 4px 12px rgba(0,0,0,0.24)',
    ...Array(19).fill('none'),
  ] as unknown as ThemeOptions['shadows'],
});

// Light theme
export const lightTheme = createTheme({
  ...commonTheme,
  palette: {
    mode: 'light',
    primary: { main: '#6750A4', light: '#EADDFF', dark: '#4F378B' },
    secondary: { main: '#625B71', light: '#E8DEF8', dark: '#4A4458' },
    error: { main: '#B3261E' },
    background: {
      default: '#FFFBFE',
      paper: '#F3EDF7',
    },
    text: { primary: '#1C1B1F', secondary: '#49454F', disabled: '#938F99' },
    divider: '#CAC4D0',
    action: { hover: 'rgba(103, 80, 164, 0.08)', selected: 'rgba(103, 80, 164, 0.16)' },
  },
});
